<template>
  <section
    :class="getCarouselTheme(currentSlide, blok)"
    class="carousel"
    @mouseover="pauseProgressRing()"
    @mouseleave="startProgressRing()"
  >
    <vue-glide
      :per-view="1"
      :gap="0"
      :autoplay="blok.autoplay ? speed : false"
      :animation-duration="500"
      :hoverpause="true"
      type="carousel"
      @glide:move="slideTriggered"
    >
      <component
        :is="'m-sectionHeader' | dashify"
        v-if="blok.sectionHeader && blok.sectionHeader[0]"
        slot="control"
        :blok="blok.sectionHeader[0]"
        :theme="getHeaderTheme(currentSlide, blok.slides)"
        data-glide-dir=""
      />

      <button
        slot="control"
        type="button"
        data-glide-dir="<"
        class="carousel__arrow carousel__arrow--prev"
      >
        <div class="icon icon--arrow"></div>
      </button>

      <button
        slot="control"
        type="button"
        data-glide-dir=">"
        class="carousel__arrow carousel__arrow--next"
      >
        <div class="icon icon--arrow"></div>
      </button>

      <a-progress-ring
        v-if="blok.autoplay"
        slot="control"
        :progress="progress"
        radius="28"
        stroke="2"
      ></a-progress-ring>

      <vue-glide-slide v-for="(slide, key) in blok.slides" :key="key">
        <component
          :is="'m-slide' | dashify"
          :slide="slide"
          :is-h-screen="blok.isHScreen"
          :slide-grid="getSlideGrid(blok)"
          :visible="visible"
        />
      </vue-glide-slide>
    </vue-glide>
  </section>
</template>

<script>
import { Glide, GlideSlide } from 'vue-glide-js'
import 'vue-glide-js/dist/vue-glide.css'
import AProgressRing from '~/components/atoms/a-svgs/a-svg-progress'

/*
 *  Docs:
 *  https://glidejs.com/docs/
 *  https://www.npmjs.com/package/vue-glide-js
 */

export default {
  name: 'Carousel',
  components: {
    [Glide.name]: Glide,
    [GlideSlide.name]: GlideSlide,
    AProgressRing,
  },
  props: {
    blok: {
      type: Object,
      default: () => {},
    },
    visible: {
      type: Boolean,
      default: () => false,
    },
    visibleSectionIndex: {
      type: Number,
      default: () => 1000,
    },
  },
  data: () => ({
    active: true,
    progress: 0,
    interval: false,
    speed: 5000,
    currentSlide: 0,
  }),
  watch: {
    visible(visible) {
      if (visible) {
        this.$store.commit(
          'globals/setVisibleSectionName',
          this.blok.breadcrumb
        )
        this.$store.commit(
          'globals/setVisibleSectionIndex',
          this.visibleSectionIndex
        )
      }
    },
  },
  mounted() {
    this.startProgressRing()
    // add container class to glide controller (to keep content within layout)
    this.$el
      .querySelector('[data-glide-el="controls"]')
      .classList.add('container')
  },
  methods: {
    startProgressRing() {
      this.active = true
      this.progress = 0
      clearInterval(this.interval)

      this.interval = setInterval(() => {
        this.progress += 1
        if (this.progress === 100) {
          clearInterval(this.interval)
        }
      }, 50)
    },
    pauseProgressRing() {
      this.active = false
      clearInterval(this.interval)
    },
    slideTriggered() {
      this.active = false
      setTimeout(() => {
        this.active = true
      }, 500)
      this.startProgressRing()

      this.currentSlide = this.$children[0].currentSlide
    },
    getCarouselTheme(slideId, data) {
      const height = data.isHScreen ? ' h-screen' : 'h-default'
      return `${data.slides[slideId].theme} ${height} ${data.variation}${
        this.active && data.autoplay ? ' carousel--loading' : ''
      }`
    },
    getHeaderTheme(slideId, slides) {
      if (slides[slideId].theme === 'slide__dark') {
        return 'theme-dark'
      }
      return 'theme-gray-light'
    },
    getSlideGrid(data) {
      switch (data.variation) {
        case 'carousel--hero':
        case 'carousel--hero-middle':
          return 'col-12 col-md-8'
        default:
          return 'col-12 col-md-6'
      }
    },
  },
}
</script>

<style lang="scss">
@import '../../assets/scss/settings';

$icon-gap: 10px;
$icon-height: 54px;
$icon-width: 54px;

.carousel {
  position: relative;

  &.carousel--hero,
  &.carousel--hero-middle {
    .slide {
      background-image: linear-gradient(
        45deg,
        rgba(0, 0, 0, 0.8) 0%,
        rgba(0, 0, 0, 0) 87%
      );
      background-color: transparent;
    }

    .slide__content {
      .slide__content-left {
        padding-top: spacer(3);
      }

      .slide__content-right {
        display: none;
      }
    }
  }

  &.slide__dark {
    .carousel__arrow {
      color: $white;

      &:focus {
        color: $white;
      }

      &:active,
      &:hover {
        color: $oslo-gray;
      }
    }

    .progress-ring__circle {
      stroke: $white;
    }
  }

  &.slide__light {
    .carousel__arrow {
      color: $oslo-gray;

      &:focus {
        color: $oslo-gray;
      }

      &:active,
      &:hover {
        color: $mine-shaft;
      }
    }

    .progress-ring__circle {
      stroke: $oslo-gray;
    }
  }

  .progress-ring {
    opacity: 0;
    position: absolute;
    top: calc(100vh - 168px);
    right: calc(50% - 59px);
  }

  &--loading {
    .progress-ring {
      opacity: 1;
    }
  }

  // Native glide component modifiers
  .glide {
    display: flex;
    flex-wrap: wrap;
  }

  .glide__track {
    order: 2;
  }

  div[data-glide-el='controls'] {
    order: 1;
  }
}

/**
     * Carousel nav buttons
     * For a11y these need to be `<button>`s
     */
.carousel__arrow {
  background-color: transparent;
  border: 1px solid;
  border-radius: 50%;
  cursor: pointer;
  height: $icon-height;
  padding: 0;
  position: absolute;
  top: calc(100vh - 168px);
  transition: $transition-base;
  width: $icon-width;
  z-index: 100;
  outline: none;

  .icon--arrow {
    &::before {
      @include font-size(9, rem-v(9));

      display: block;
    }
  }

  &--prev {
    right: calc(50% + 5px);

    .icon--arrow {
      transform: rotate(180deg);
    }
  }

  &--next {
    right: calc(50% - 59px);

    .carousel--loading & {
      border-color: transparent;
    }
  }
}

/* Carousel slide content */

.glide__slides {
  margin: 0;
}

.carousel .section-header {
  position: absolute;
  top: 35px;
  width: 100%;
  background: transparent;
}

// breakpoints
@include media-breakpoint-up(md) {
  .carousel {
    .progress-ring {
      right: spacer(2);
      top: 20px;
    }

    &.carousel--hero {
      .carousel__arrow {
        top: calc(50% - 27px);
      }

      .progress-ring {
        top: calc(50% - 27px);
      }
    }

    &.carousel--hero,
    &.carousel--hero-middle {
      .slide__content {
        .slide__content-left {
          padding-top: 0;
          justify-content: center;
        }
      }
    }
  }

  .carousel__arrow {
    top: 20px;

    &--prev {
      right: $icon-height + spacer(2) + $icon-gap;
    }

    &--next {
      right: spacer(2);
    }
  }
}

@include media-breakpoint-up(lg) {
  .carousel {
    &.carousel--hero,
    &.carousel--hero-middle {
      .slide__title {
        @include font-size(83, rem-v(96));
      }
    }
  }
}
</style>
